/* exported Validation */
/* global validate */
/*************************** Validation ******************************/
/*
    For our form validation we use validateJs. Documentation can be found at: http://validatejs.org/
    Every form should have it's own JS file.
    To trigger validation create an object with the configuration

    const validation = new Validation({
        selector: '.form',                  (required| string) : The selector of the form
        constraints:  email: {              (required | obj) : The rules for validating the fields http://validatejs.org/#constraints
            presence: true,
            email: true
        }
        onValidationSubmit: function () {}   (optional | function) : Callback function that is fired when form is submitted.
     });
 */

import getTranslation from "helpers/translation/getTranslation";
import { trackEvent } from "vendor/google/gtm/gtmEventTracker";
import {
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  validatePhoneNumberLength,
} from "libphonenumber-js";

//Check if validate exists
if (typeof validate !== "undefined") {
  /*--------------------------- DEFAULT SETTINGS FOR VALIDATEJS ---------------------------*/
  //Add default settings for validateJS. Documentation: http://validatejs.org/#validate-default-options
  validate.options = { fullMessages: false, format: "grouped" };

  //Set custom messages
  validate.validators.presence.message = getTranslation(
    "validation",
    "CANT_BE_EMPTY",
  );
  validate.validators.email.message = getTranslation(
    "validation",
    "IS_INVALID",
  );
  validate.validators.length.wrongLength = getTranslation(
    "validation",
    "INVALID_WRONG_LENGTH",
  );
  validate.validators.length.tooLong = getTranslation(
    "validation",
    "INVALID_TOO_LONG",
  );
  validate.validators.length.tooShort = getTranslation(
    "validation",
    "INVALID_TOO_SHORT",
  );
  validate.validators.numericality.notGreaterThanOrEqualTo = getTranslation(
    "validation",
    "INVALID_GREATERTHAN_OR_EQUALTO",
  );
  validate.validators.numericality.notLessThanOrEqualTo = getTranslation(
    "validation",
    "INVALID_LESSTHAN_OR_EQUALTO",
  );
  validate.validators.numericality.notInteger = getTranslation(
    "validation",
    "INVALID_NOT_INTEGER",
  );
  validate.validators.numericality.notValid = getTranslation(
    "validation",
    "INVALID_NOT_INTEGER",
  );
  validate.validators.type.messages.azText = getTranslation(
    "validation",
    "INVALID_AZ_TEXT",
  );
  validate.validators.type.messages.azTextWithSpaces = getTranslation(
    "validation",
    "INVALID_AZ_TEXT_WITH_SPACES",
  );
  validate.validators.type.messages.alphaNumericTextWithSpaces = getTranslation(
    "validation",
    "INVALID_ALPHANUMERIC_TEXT_WITH_SPACES",
  );
  validate.validators.type.messages.street = getTranslation(
    "validation",
    "IS_INVALID",
  );
  /*--------------------------- CUSTOM TYPES ---------------------------*/
  // Regex check that only allows alphabetical characters
  validate.validators.type.types.azText = (value) => {
    if (/^[a-z.'\-\u00E0-\u00FC]+$/i.test(value)) {
      return true;
    }
    return false;
  };

  // Regex check that only allows alphabetical characters including spaces
  validate.validators.type.types.azTextWithSpaces = (value) => {
    if (/^[\p{L}’ .'\-]+$/u.test(value)) {
      return true;
    }
    return false;
  };

  // Regex check that only allows alphabetical characters including spaces
  validate.validators.type.types.alphaNumericTextWithSpaces = (value) => {
    if (/^[\p{L}0-9’ .'\-]+$/u.test(value)) {
      return true;
    }
    return false;
  };

  // Regex check for street
  validate.validators.type.types.street = (value) => {
    if (/^[a-z0-9&\s'.\-\u00E0-\u00FC]+$/i.test(value)) {
      return true;
    }
    return false;
  };

  /*--------------------------- CUSTOM CONTRAINTS ---------------------------*/
  //If custom validators are created add them here. Documentation: http://validatejs.org/#custom-validator

  validate.validators.phoneNumber = (inputValue, options) => {
    const value = inputValue ? inputValue : "";
    const countryCode = options.input.parentElement.dataset.countryCode;

    // Do not validate a already saved address
    if (options.isSavedAddress && options.isSavedAddress.checked) {
      return;
    }

    const isValid =
      !value.startsWith("00") &&
      !value.startsWith("+") &&
      isPossiblePhoneNumber(value, countryCode) &&
      isValidPhoneNumber(value, countryCode);

    if (!isValid) {
      const message = getTranslation("validation", "IS_INVALID");

      return message ? message : "is not valid";
    }
  };

  /*PASSWORD VALIDATOR -> Validator for validating a password field*/
  validate.validators.password = (value, options) => {
    //Validate field using existing validators
    const messages = validate.single(value, {
      format: {
        pattern: "(?=.*?[0-9])(?=.*?[A-Za-z]).+",
        message: getTranslation("validation", "INVALID_PASSWORD_FORMAT"),
      },
      length: {
        minimum: 6,
      },
    });

    // TODO: in Gulp file 'this' refers to window object
    // TODO: in this file 'this' is undefined, removing this.message fixes the script
    // TODO: check on later moment if this.message can be removed entirely or we need to rewrite this script
    const message = options.message || messages; //Set a custom message or use the messages provided in messages
    // const message = options.message || this.message || messages; //Set a custom message or use the messages provided in messages

    //Check if there are any messages
    if (messages) {
      return message;
    }
  };

  /*Year VALIDATOR -> Validator for validating a year (YYYY)*/
  validate.validators.year = (value, options) => {
    //Validate field using existing validators
    const messages = validate.single(value, {
      length: { is: 4 },
      numericality: {
        onlyInteger: true,
      },
    });

    // TODO: in Gulp file 'this' refers to window object
    // TODO: in this file 'this' is undefined, removing this.message fixes the script
    // TODO: check on later moment if this.message can be removed entirely or we need to rewrite this script
    const message = options.message || messages; //Set a custom message or use the messages provided in messages
    // const message = options.message || this.message || messages; //Set a custom message or use the messages provided in messages/

    //Check if there are any messages
    if (messages) {
      return message;
    }
  };

  /*Month VALIDATOR -> Validator for validating a month (MM)*/
  validate.validators.month = (value, options) => {
    //Validate field using existing validators
    const messages = validate.single(value, {
      length: { minimum: 1, maximum: 2 },
      numericality: {
        onlyInteger: true,
        greaterThanOrEqualTo: 1,
        lessThanOrEqualTo: 12,
      },
    });

    // TODO: in Gulp file 'this' refers to window object
    // TODO: in this file 'this' is undefined, removing this.message fixes the script
    // TODO: check on later moment if this.message can be removed entirely or we need to rewrite this script
    const message = options.message || messages; //Set a custom message or use the messages provided in messages
    // const message = options.message || this.message || messages; //Set a custom message or use the messages provided in messages/

    //Check if there are any messages
    if (messages) {
      return message;
    }
  };

  /*Day VALIDATOR -> Validator for validating a day (DD)*/
  validate.validators.day = (value, options) => {
    //Validate field using existing validators
    const messages = validate.single(value, {
      length: { minimum: 1, maximum: 2 },
      numericality: {
        onlyInteger: true,
        greaterThanOrEqualTo: 1,
        lessThanOrEqualTo: 31,
      },
    });

    // TODO: in Gulp file 'this' refers to window object
    // TODO: in this file 'this' is undefined, removing this.message fixes the script
    // TODO: check on later moment if this.message can be removed entirely or we need to rewrite this script
    const message = options.message || messages; //Set a custom message or use the messages provided in messages
    // const message = options.message || this.message || messages; //Set a custom message or use the messages provided in messages/

    //Check if there are any messages
    if (messages) {
      return message;
    }
  };

  validate.validators.bancontactNumber = (value) => {
    const message = getTranslation("validation", "IS_INCORRECT");
    if (!/^(4796|4810|4871|5613|5614|6060|6703)[0-9]{12,15}$/.test(value)) {
      return message;
    }
  };

  validate.validators.postalCode = (value, options) => {
    const isB2B = document.querySelector(".register-form-b2b");
    const messageB2B = getTranslation(
      "validation",
      "IS_INCORRECT_POSTALCODE_B2B",
    );
    const message = getTranslation("validation", "IS_INCORRECT_POSTALCODE");
    let regex = null;
    const siteViewId = parseInt(options.siteviewId);

    switch (siteViewId) {
      case 1:
      case 3:
        regex = /^[0-9]{4}[ ]?[a-zA-Z]{2}$/;
        break;
      case 2:
      case 4:
        regex = /^[0-9]{4}$/;
        break;
      case 7:
        regex = /^(F-)?[0-9]{5}$/;
        break;
      default:
        regex = /$/i;
    }

    if (!regex.test(value)) {
      if (!isB2B) {
        return message;
      } else {
        return messageB2B;
      }
    }
  };

  validate.validators.houseNumber = (value, options) => {
    const message = getTranslation("validation", "CANT_BE_EMPTY");
    const siteViewId = parseInt(options.siteviewId);

    switch (siteViewId) {
      case 7:
        return;
      default:
        if (!value) {
          return message;
        }
    }
  };

  validate.validators.ibanNumber = (value) => {
    const message = getTranslation("validation", "IS_INCORRECT");

    if (typeof value === "undefined" || value === null) {
      return message;
    }

    // Convert to uppercase and remove all non alphanumeric characaters
    const sanitizedIban = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    // Move county definition (first 2 chars + 2 checkdigits) to the back of the string
    const rearrangedIban = sanitizedIban.replace(
      /^([A-Z]{2})([0-9]{2})(.+)$/,
      "$3$1$2",
    );
    // Replace all characters with 2 digits (A = 10, B = 11, ..., Z = 35
    const numericalIban = rearrangedIban.replace(/[A-Z]/g, function (letter) {
      return String(letter.charCodeAt(0) - 55);
    });

    const modulo97 = (numericalIban) => {
      let moduloResult = numericalIban.substr(0, 2);
      numericalIban = numericalIban.substr(2);

      while (numericalIban.length > 0) {
        const workingPart = moduloResult + numericalIban.substr(0, 7);
        numericalIban = numericalIban.substr(7);
        moduloResult = workingPart % 97;
      }
      return moduloResult;
    };

    const isValid = modulo97(numericalIban) === 1;

    if (!isValid) {
      return message;
    }
  };

  /*CHECKBOX CHECKED  -> validator to check if minimal 1 checkbox is checked */
  validate.validators.checkedCheckbox = (value, options) => {
    const checkboxContainer = document.querySelector(options.checkboxContainer);
    const checkedCheckboxes = checkboxContainer
      ? checkboxContainer.querySelectorAll('input[type="checkbox"]:checked')
      : [];

    //Validate field using existing validators
    const messages = validate.single(checkedCheckboxes.length, {
      numericality: {
        greaterThanOrEqualTo: options.minCheckedItems,
        notGreaterThanOrEqualTo: getTranslation(
          "validation",
          "MIN_SELECT_ITEMS",
        ),
      },
    });

    // TODO: in Gulp file 'this' refers to window object
    // TODO: in this file 'this' is undefined, removing this.message fixes the script
    // TODO: check on later moment if this.message can be removed entirely or we need to rewrite this script
    const message = options.message || messages; //Set a custom message or use the messages provided in messages
    // const message = options.message || this.message || messages; //Set a custom message or use the messages provided in messages/

    //Select checkbox container
    if (checkboxContainer) {
      const checkboxes = checkboxContainer.querySelectorAll(
        'input[type="checkbox"]',
      ); //Get all checkboxes
      if (checkboxes.length > 0) {
        //Update checkbox styles
        checkboxes.forEach((checkbox) => {
          if (!message) {
            setTimeout(() => {
              checkbox.classList.remove("is-valid");
              checkbox.classList.remove("is-invalid");
            }, 0);
          }
        });
      }
    }

    //Check if there are any messages
    if (messages) {
      return message;
    }
  };

  /*OrderNumber VALIDATOR -> Validator for validating an ordernumber*/
  validate.validators.orderNumber = (value, options) => {
    //Validate field using existing validators
    const messages = validate.single(value, {
      numericality: true,
      length: {
        maximum: 10,
      },
    });

    // TODO: in Gulp file 'this' refers to window object
    // TODO: in this file 'this' is undefined, removing this.message fixes the script
    // TODO: check on later moment if this.message can be removed entirely or we need to rewrite this script
    const message = options.message || messages; //Set a custom message or use the messages provided in messages
    // const message = options.message || this.message || messages; //Set a custom message or use the messages provided in messages/

    //Check if there are any messages
    if (messages) {
      return message;
    }
  };

  /* VATNumber VALIDATOR -> Validator for validating a european vatnumber */
  validate.validators.vatNumber = (value) => {
    const regex =
      /^((AT)?\s?U[0-9]{8}|(BE)?\s?[0-9]{10}|(BG)?\s?[0-9]{9,10}|(CY)?\s?[0-9]{8}L|(CZ)?\s?[0-9]{8,10}|(DE)?\s?[0-9]{9}|(DK)?\s?[0-9]{8}|(EE)?\s?[0-9]{9}|(EL|GR)?\s?[0-9]{9}|(ES)?\s?[0-9A-Z][0-9]{7}[0-9A-Z]|(FI)?\s?[0-9]{8}|(FR)?\s?[0-9A-Z]{2}[0-9]{9}|(GB)?\s?([0-9]{9}([0-9]{3})?\s?|[A-Z]{2}[0-9]{3})|(HU)?\s?[0-9]{8}|(IE)?\s?[0-9]S[0-9]{5}L|(IT)?\s?[0-9]{11}|(LT)?\s?([0-9]{9}|[0-9]{12})|(LU)?\s?[0-9]{8}|(LV)?\s?[0-9]{11}|(MT)?\s?[0-9]{8}|(NL)?\s?[0-9]{9}B[0-9]{2}|(PL)?\s?[0-9]{10}|(PT)?\s?[0-9]{9}|(RO)?\s?[0-9]{2,10}|(SE)?\s?[0-9]{12}|(SI)?\s?[0-9]{8}|(SK)?\s?[0-9]{10})$/;
    if (value !== "" && value !== null && !regex.test(value)) {
      return getTranslation("validation", "IS_INCORRECT_VATNUMBER");
    }
  };

  /* File size VALIDATOR -> Validator for validating the size of a file */
  validate.validators.fileInputSize = (value, options) => {
    const { inputSelector, maxFileSize } = options;

    if (inputSelector && inputSelector.files[0]) {
      const fileSize = inputSelector.files[0].size / 1024 / 1024; // in MiB
      if (fileSize > maxFileSize) {
        return getTranslation("validation", "FILE_TOO_LARGE");
      }
    }
    return null;
  };

  validate.validators.skipIfFieldExists = (value, options, key, attributes) => {
    const field = document.querySelector(options.fieldSelector);

    if (field) {
      // Skip validation by returning nothing
      return;
    }
    // Perform normal validation if the field is not present
    return validate.validators.presence.call(
      this,
      value,
      options,
      key,
      attributes,
    );
  };

  validate.validators.yearFormat = (value) => {
    if (/^\d{2}$/.test(value)) {
      return;
    }
    return getTranslation("validation", "INVALID_PATTERN");
  };
}

/*--------------------------- HANDLE VALIDATION ---------------------------*/
export const Validation = function ({ form, constraints, onValidationSubmit }) {
  this.config = {
    validClass: "is-valid",
    invalidClass: "is-invalid",
    invalidFeedbackClass: "invalid-feedback",
    constraints,
    formElement: form
      ? document.querySelector(form)
      : document.querySelector(".form"),
    onValidationSubmit,
  };

  this.initValidation = () => {
    /* Check if form and constraints exist */
    if (
      this.config.formElement &&
      constraints &&
      typeof validate !== "undefined"
    ) {
      // Hook up the form so we can prevent it from being posted
      const formElement = this.config.formElement;
      formElement.noValidate = true; //Set novalidate attribute on form to disable native validation
      formElement.addEventListener("submit", (event) => {
        event.preventDefault();
        this.handleFormSubmit(this);
      });

      this.setChangeListeners();
    }
  };

  this.setChangeListeners = () => {
    // Hook up the inputs to validate on the fly
    const inputs = this.config.formElement.querySelectorAll("[data-validate]");

    inputs.forEach((input) => {
      if (!input.getAttribute("no-live-validate")) {
        // Listen to 'change' event our custom 'postLiveFormat' event (fired from 'helpers/forms/live-format')
        ["change", "postLiveFormat"].map((eventName) =>
          input.addEventListener(eventName, (event) => {
            const errors = this.getErrors(
              event.target.getAttribute("data-validate"),
            );
            this.showFeedbackForInput(event.target, errors);
          }),
        );
      }
    });
  };
};

/*--------------------------- PROTOTYPES ---------------------------*/
/* PROTOTYPE: Handle form submit */
Validation.prototype.handleFormSubmit = function () {
  // validate the form against the constraints
  const errors = this.getErrors();

  //Check if there are any errors
  if (errors) {
    // add invalid class - remove valid class to form
    this.config.formElement.classList.add(this.config.invalidClass);
    this.config.formElement.classList.remove(this.config.validClass);

    const errorsToCheck = Object.keys(errors); //Get the errors fields that need to be checked
    for (const error of errorsToCheck) {
      const inputs = this.config.formElement.querySelectorAll(
        `[data-validate=${error}]`,
      ); //Get the inputs
      //Check if the input exists
      if (inputs) {
        this.showFeedbackForInput(inputs, errors[error]); //Show feedback per input
      }
    }
  } else {
    // add valid class - remove invalid class to form
    this.config.formElement.classList.add(this.config.validClass);
    this.config.formElement.classList.remove(this.config.invalidClass);

    // If a callback is given, execute it
    if (typeof this.config.onValidationSubmit === "function") {
      this.config.onValidationSubmit(this);
    } else {
      this.config.formElement.submit(); //If there is no callback, just submit the form
    }
  }
};

/* PROTOTYPE: Get errors */
Validation.prototype.getErrors = function (validator) {
  let errors = null;

  const inputs = this.config.formElement.querySelectorAll(
    '[data-validate]:not([type="radio"])',
  ); //Get all inputs which need to be validated
  const radios = this.config.formElement.querySelectorAll(
    '[data-validate][type="radio"]',
  ); //Get all radios which need to be validated
  const values = {}; //Create empty object to store values of elements

  //Create object based on data-validate attributes
  inputs.forEach((input) => {
    const key = input.getAttribute("data-validate"); //Get the value of the data-validate which should be the same as the constraint to check
    values[key] = input.value ? input.value : null; //Check if there is a value otherwise set it to null for the prescense check
  });

  radios.forEach((input) => {
    const key = input.getAttribute("data-validate"); //Get the value of the data-validate which should be the same as the constraint to check
    if (input.checked) {
      values[key] = input.value;
    }
  });

  if (Object.keys(values).length > 0) {
    errors = validate(values, this.config.constraints) || null; //Get the errors, if there are no errors set it to null
    if (validator && errors) {
      errors = errors[validator]; //Only return the error related to the field
    }
  }

  return errors;
};

/* PROTOTYPE: Show error for input */
Validation.prototype.showFeedbackForInput = function (elements, errors) {
  let elementsList = []; // store elements in array

  // if elements is a nodelist, simply copy to variable
  if (elements.length) {
    elementsList = elements;
  } else {
    elementsList.push(elements);
  }

  // Reset old validation if exist
  this.resetFormValidation(elementsList);

  // if there are any errors
  if (errors) {
    const invalidClass = this.config.invalidClass;

    // if the element is a selectbox add class to element itself instead of child nodes (which are options in this case)
    if (elementsList.nodeName === "SELECT") {
      elementsList.classList.add(invalidClass); //Add invalid class to element
    } else {
      // loop through all
      elementsList.forEach(function (item) {
        item.classList.add(invalidClass); //Add invalid class to element
      });
    }

    // create errorMessage
    this.createErrorMessage(errors, elementsList);
  } else {
    // no errors found
    // check if the input is a radio
    if (elementsList[0].type === "radio") {
      // select all inputs with the same data-validate value (radios all have the same constraint)
      const constraint = elementsList[0].getAttribute("data-validate");
      const inputs = this.config.formElement.querySelectorAll(
        `[data-validate=${constraint}]:not(:checked)`,
      );
      // reset validation messages for all radios with the same constraint
      this.resetFormValidation(inputs);
    }
    elementsList[0].classList.add(this.config.validClass); //Add valid class to for element
    const validateMessageContainer = document.querySelector(
      `[data-validate-parent='${elementsList[0].dataset.validate}']`,
    );
    if (validateMessageContainer) {
      validateMessageContainer.remove();
    }
  }
};

/* PROTOTYPE: Create error for input */
Validation.prototype.createErrorMessage = function (errors, elementsList) {
  //Create message element
  const messageElement = document.createElement("div");
  //Add invalid feedback class to element
  messageElement.classList.add(this.config.invalidFeedbackClass);

  //Variable to determine if we want to use the parent or the first child
  let targetElement;

  // if elementsList is a selectbox, target the parent element (because the first child is an <option>)
  if (elementsList.nodeName === "SELECT") {
    targetElement = elementsList;
  } else {
    // else target the first child
    targetElement = elementsList[0];
  }

  // add data-validate from input to data-validate-parent attribute on messageElement
  messageElement.dataset.validateParent = targetElement.dataset.validate;

  // put errors in new variable
  let errorMessages = errors;

  //If there are multiple messages, show errors in a list.
  if (errors.length > 1) {
    // reset errorMessages
    errorMessages = "";
    // loop through errors object
    for (const error in errors) {
      // add new element to errorMessages string
      errorMessages += `<span class="list-item">${this.addPrefix(targetElement, errors[error])}</span>`;
    }
  } else {
    errorMessages = `<span class="list-item">${this.addPrefix(targetElement, errorMessages[0])}</span>`;
  }

  //Add error text to element
  messageElement.innerHTML = errorMessages;

  if (targetElement.hasAttribute("data-validate-message-container")) {
    const messageContainerId = targetElement.getAttribute(
      "data-validate-message-container",
    ); //Get the id
    // check if there's already an element that contains the error message for this field
    const msgElValidateParent = messageElement.dataset.validateParent;
    const msgEl = document.querySelector(
      `.invalid-feedback[data-validate-parent="${msgElValidateParent}"]`,
    );
    // if this element already exists, delete it so we don't show duplicate errors
    if (msgEl) {
      msgEl.remove();
    }
    //Insert message element in container
    document
      .getElementById(messageContainerId)
      .insertAdjacentElement("beforeend", messageElement);
  } else {
    //Insert message element after input element
    targetElement.insertAdjacentElement("afterend", messageElement);
  }

  // send error to Analytics
  this.pushErrorToAnalytics(errors, targetElement);
};

/* PROTOTYPE: Add prefix */
Validation.prototype.addPrefix = function (element, error) {
  // set variable
  let prefix = null;
  // find label value
  // (does not work for radio buttons because the input label is not the one describing what you want as a prefix, use data-validate-prefix there)
  const closestLabel = element.previousElementSibling;

  if (element.hasAttribute("data-validate-prefix")) {
    const value = element.getAttribute("data-validate-prefix");
    if (value && value !== "false") {
      prefix = value;
    }
  } else if (closestLabel) {
    // get text from label
    let closestLabelText = closestLabel.textContent;

    // format closestLabelText to strip * if it has any
    if (closestLabelText.slice(-2) === " *") {
      closestLabelText = closestLabelText.substring(
        0,
        closestLabelText.length - 2,
      );
    }

    // set labelText as value for prefix
    prefix = closestLabelText;
  }

  // if prefix is true
  if (prefix) {
    let errorMsg = error; // put error in variable for further manipulation
    // add prefix to string
    errorMsg = prefix + " " + errorMsg;
    // return error message
    return errorMsg;
  } else {
    // else do not prefix
    return error;
  }
};

/* PROTOTYPE: Create error for input */
Validation.prototype.pushErrorToAnalytics = function (errors, element) {
  // set error message for Analytics
  const fieldName = element.getAttribute("data-validate"); //Get the field name

  // If there is an error message, send it to Analytics
  if (fieldName) {
    errors.forEach((error) =>
      trackEvent("Error", fieldName, this.addPrefix(element, error)),
    ); //Send events per error
  }
};

/* PROTOTYPE: Reset validation */
Validation.prototype.resetFormValidation = function (elements) {
  // store 'this' inside variable to use it in forEach loop
  const self = this;

  // if element is a selectbox reset input of parent (because child nodes are <option> items)
  if (elements.nodeName === "SELECT") {
    this.resetFormInput(elements);
  } else {
    // else reset all child nodes
    elements.forEach(function (item) {
      self.resetFormInput(item);
    });
  }
};

Validation.prototype.resetFormInput = function (input) {
  const validClass = this.config.validClass;
  const invalidClass = this.config.invalidClass;
  let messageHolder = null;

  input.classList.remove(invalidClass); //Remove invalid class
  input.classList.remove(validClass); //Remove valid class

  //Check if input has nextElement sibling and has invalid-feedback class
  if (
    input.nextElementSibling &&
    input.nextElementSibling.classList.contains("invalid-feedback")
  ) {
    messageHolder = input.nextElementSibling;
  } else if (typeof input.dataset.validateMessageContainer !== "undefined") {
    messageHolder = document.querySelector(
      "#" + input.dataset.validateMessageContainer,
    ).firstChild;
  }

  if (messageHolder) {
    messageHolder.remove();
  }
};

/*
 *   Function to check if the server side also validated successfully.
 *   params:
 *       - data          response from the server. we have rules for how the server should
 *                       return this (https://relinks.myjetbrains.com/youtrack/issue/SLS-213)
 *       - onSuccess     Callback which will be called when the server side validation is successfully.
 *       - onError       Callback which is called when the server side validation failed
 */
Validation.prototype.handleServerSideErrors = function (
  data,
  onSuccess,
  onError,
) {
  if (data.success) {
    // form was validated successfully in the server side, call the callback to continue the form post.
    if (typeof onSuccess === "function") {
      onSuccess();
    }
    return true;
  }
  // store the server errors in a variable
  const errors = data.messages.form;
  if (errors) {
    Object.keys(errors).forEach((error) => {
      // Find the element that belongs to this error
      const elements = this.config.formElement.querySelectorAll(
        `[name="${error}"]`,
      );

      elements.forEach((el) => {
        // show the error
        this.showFeedbackForInput(el, errors[error]);
      });
    });
  }

  // call the callback if there is any
  if (typeof onError === "function") {
    onError();
  }
};
